/**
 * A counted phrase in the form its number asks for.
 *
 * Czech declines the noun with the number — "1 noc", "2 noci", "5 nocí" — so a
 * `"{count} nocí"` template is wrong for every count from two to four, the
 * commonest stays there are. English needs only the singular and the plural;
 * its `few` form is the plural, carried so both locales share one shape.
 *
 * A count written with decimals has a category of its own in Czech — "0,1
 * rezervace", the genitive singular — which `Intl.PluralRules` calls `many`;
 * it is optional, and a phrase that never counts fractions falls back to
 * `other` for it. The plural is chosen for the number as it is written, so
 * `fractionDigits` shapes both the digits shown and the form picked.
 */
export interface PluralForms {
    /** 1 — "{count} noc" / "{count} night". */
    one: string;
    /** 2–4 in Czech — "{count} noci". */
    few: string;
    /** A fractional count in Czech — "{count} rezervace". Falls back to `other`. */
    many?: string;
    /** Everything else — "{count} nocí" / "{count} nights". */
    other: string;
}

export interface PluralFormOptions {
    /** Write the count with exactly this many decimals — and decline it as written. */
    fractionDigits?: number;
}

export function pluralForm(locale: string, count: number, forms: PluralForms, options: PluralFormOptions = {}): string {
    const digits = options.fractionDigits != null
        ? { minimumFractionDigits: options.fractionDigits, maximumFractionDigits: options.fractionDigits }
        : {};
    const category = new Intl.PluralRules(locale, digits).select(count);
    const template = category === "one"
        ? forms.one
        : category === "few"
            ? forms.few
            : category === "many"
                ? forms.many ?? forms.other
                : forms.other;
    return template.replace("{count}", new Intl.NumberFormat(locale, digits).format(count));
}
